/*
  !Important!
  We've updated these settings to allow for customization of language for "Enforce Data Privacy Acceptance", 
  however there's no reason it couldn't work for all settings with the implementation. 

  When we re-do this UI for Apollo (hopefully in the near future ~ early 2025), we should only be using the values array for 
  values, and value should be considered deprecated. 

  Since this is now throwaway code, I didn't want to refactor the UI completley, and made it work for the specific 
  setting that is using the values array but it isn't the best way to do it when starting over. 
*/

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';

// Services
import { AuthService } from '@app/shared/services/auth.service';
import { OrgSettingsService } from '@app/orgs/services/org-settings.service';
import { TranslateService } from '@ngx-translate/core';

// Types
import { AuthUser } from '@app/account/account-api.model';
import { OrgSetting, OrgSettingValue } from '@app/orgs/services/orgs.model';
import { ConfigurationValue } from '@app/orgs/configuration-value';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToggleSwitchComponent } from '@app/shared/components/toggle-switch/toggle-switch.component';
import { DfIconModule, DfSpinnerModule } from '@lib/fresco';
import { SettingTileToggleTextComponent } from '../setting-tile-toggle-text/setting-tile-toggle-text.component';
import { LanguageService } from '@app/translation/services/language.service';
import { LanguageLocale } from '@app/inputs/inputs.model';
import { SharedModule } from '@app/shared/shared.module';
import { ComboboxOption } from '@app/shared/components/combobox/combobox.model';
import { Observable, switchMap, tap } from 'rxjs';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { ModalService } from '@app/shared/services/modal.service';

interface Option extends ComboboxOption {
  name: string;
}

enum SettingId {
  EnforceDataPrivacyAcceptance = 'EnforceDataPrivacyAcceptance',
}

@Component({
    selector: 'dgx-setting-tile',
    templateUrl: './setting-tile.component.html',
    styleUrls: ['./setting-tile.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        DfIconModule,
        DfSpinnerModule,
        SharedModule, // needed for comboxbox
        //components
        ToggleSwitchComponent,
        SettingTileToggleTextComponent,
    ]
})
export class SettingTileComponent implements OnInit {
  @Input() public setting: OrgSetting;
  @Input() public orgId: number;
  @Input() public isMessageSetting: boolean;
  @Input() public isGroupedSetting: boolean;
  @Input() public configurationValue: ConfigurationValue;

  @Output() public settingToggled = new EventEmitter();
  @Output() public textUpdated = new EventEmitter();

  public defaultMessage: string;
  public isSaved: boolean;
  public message: string;
  public originalMessage: string;
  public showToggleSwitch: boolean;
  public reloadingSetting: boolean = false;

  public readonly ConfigurationValue = ConfigurationValue;
  public readonly settingId = SettingId;
  public isEnableAISkillInference: boolean;
  public showResetAcceptanceLink: boolean;

  public i18n = this.translateService.instant([
    'Channel_SettingMessageLabel',
    'Channel_SettingPasswordLabel',
    'Channel_SettingPasswordPlaceholder',
    'Channel_SettingMessagePlaceholder',
    'Channel_SettingHeaderDescription',
    'Channel_SettingDescription',
    'Channel_SettingReveal',
    'Channel_SettingsNotSaved',
    'Core_SettingsSaved',
    'OrgSettings_ResetAcceptance',
    'OrgSettings_ResetPrivacyAcceptanceHeader',
    'dgFlexRow_EditTextSection',
    'dgOrgInternalContentForm_SelectLanguage',
    'dgOrgInternalContentForm_NoLanguageFound',
    'Core_Language',
    'Core_Active',
    'Core_LearnMore',
    'Core_ResetDefault',
  ]);

  // custom setting configurations
  public customSettings: string[];
  public systemLanguages: Option[];
  public selectedLanguageName: string = null;

  public get multiLanguageEnabled(): boolean {
    return this.checkCustomSetting(this.settingId.EnforceDataPrivacyAcceptance);
  }

  private authUser: AuthUser;
  private selectedLocaleId: string;

  constructor(
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private authService: AuthService,
    private orgSettingsService: OrgSettingsService,
    private languageService: LanguageService,
    private webEnvironmentService: WebEnvironmentService,
    private modalService: ModalService
  ) {}

  public ngOnInit(): void {
    this.authUser = this.authService?.authUser;

    this.showResetAcceptanceLink =
      this.setting.id === 'EnforceDataPrivacyAcceptance';
    this.isEnableAISkillInference =
      this.setting.id === 'EnableAISkillInference';

    this.defaultMessage = this.getDefaultMessage();
    this.message = this.setting.value
      ? this.setting.value
      : this.defaultMessage
        ? this.defaultMessage
        : undefined;

    this.showToggleSwitch =
      this.isMessageSetting ||
      (this.setting.displayType !== 'Field' &&
        this.setting.displayType !== 'Password') ||
      (this.setting.displayType === 'Field' &&
        this.setting.enabled !== undefined);

    if (this.multiLanguageEnabled) {
      this.setupLanguages().subscribe(() => {
        this.cdr.detectChanges();
      });
    }

    this.cdr.detectChanges();
  }

  public get helpLinkUrl() {
    return this.webEnvironmentService.getZendeskUrl('/articles/14357463993628');
  }

  public getSettingName(): string {
    const settingKey = `OrgSettings_${this.setting.id}_Name`;
    const result = this.translateService.instant(settingKey);
    const settingName = result === settingKey ? this.setting.name : result;
    return settingName;
  }

  public getSettingsAccessibleEditLabel(
    setting: OrgSetting,
    value?: OrgSettingValue
  ): string {
    // If value, we need to use the sub-setting value to build the label
    // If not, we use the setting value
    if (value) {
      setting = {
        ...setting,
        id: value.id,
        value: value.value,
        name: value.popupLabel,
      };
    }

    return this.translateService.instant('A11y_Edit_Format', {
      item: this.orgSettingsService.getSettingNameTranslated(setting),
    });
  }

  public getSettingDescription(): string {
    const settingKey = `OrgSettings_${this.setting.id}_Desc`;
    const result = this.translateService.instant(settingKey);
    const settingDescription =
      result === settingKey ? this.setting.description : result;
    return settingDescription;
  }

  public getDefaultMessage(): string {
    const settingKey = `OrgSettings_${this.setting.id}_Default`;
    const result = this.translateService.instant(settingKey, {
      OrgName: this.authUser.defaultOrgInfo.name,
      OrgNameSingular: this.authUser.defaultOrgInfo.name,
    });
    const defaultMessage = result === settingKey ? undefined : result;
    return defaultMessage;
  }

  public isDefault(): boolean {
    let isDefault = false;
    if (this.setting.enabled && !this.setting.value && this.defaultMessage) {
      isDefault = this.message === this.defaultMessage ? true : false;
    }
    return isDefault;
  }

  public toggle(setting: OrgSetting, toggleValue?: boolean): void {
    setting.enabled = toggleValue ?? setting.enabled;
    this.settingToggled.emit(setting);

    // additionally only showing the auto edit modal popup if it's a setting with exactly one value
    if (
      this.isMessageSetting &&
      setting.enabled &&
      !setting.value &&
      setting.values.length === 1
    ) {
      this.showMessageEditModal(setting);
    }
  }

  public textChanged() {
    this.textUpdated.emit();
  }

  public showMessageEditModal(
    setting: OrgSetting,
    value?: OrgSettingValue
  ): void {
    let placeholder = this.i18n.Channel_SettingMessagePlaceholder;

    // override the setting with the data we care from the OrgSettingValue
    if (value) {
      setting = {
        ...setting,
        id: value.id,
        value: value.value,
        name: value.popupLabel,
      };
      this.message = setting.value;
      placeholder = value.placeholder;
    }

    this.orgSettingsService
      .showMessageEditModal(
        this.orgId,
        setting,
        this.isDefault(),
        this.defaultMessage,
        this.message,
        this.configurationValue,
        placeholder,
        this.selectedLocaleId
      )
      .subscribe({
        next: (updatedSetting: OrgSetting) => {
          if (value) {
            // this is necessary because the modal doesn't know to set the new values array
            // we'll just fix it here...
            this.setting.values.find((v) => v.id === value.id).value =
              updatedSetting.value;

            // also need to add as a configured language if it's not yet to keep the UI correct
            if (
              !this.setting.configuredLocaleIds?.includes(this.selectedLocaleId)
            ) {
              if (!this.setting.configuredLocaleIds?.length) {
                this.setting.configuredLocaleIds = [];
              }
              this.setting.configuredLocaleIds.push(this.selectedLocaleId);
              this.setupLanguages(this.selectedLocaleId).subscribe(() => {
                this.cdr.detectChanges();
              });
            }
          } else {
            this.setting = updatedSetting;
          }

          this.handleToggle();
        },
      });
  }

  public handleToggle(): void {
    if (this.setting.value) {
      this.message = this.setting.value;
    } else {
      this.message = this.defaultMessage;
    }
    this.cdr.detectChanges();
  }

  public showResetModal(): void {
    this.orgSettingsService
      .showResetPrivacyAcceptanceModal(this.orgId)
      .subscribe();
  }

  public showDeleteLanguageModal(setting: OrgSetting): void {
    const languageName = this.systemLanguages.find(
      (l) => l.id === this.selectedLocaleId
    ).name;
    const deletePhrase = 'DELETE';

    const title = this.translateService.instant('TargetCtrl_DeletePrompt', {
      targetTitle: languageName,
    });

    const deleteInstructions = this.translateService.instant(
      'TargetCtrl_DeleteInstructions',
      { delete: deletePhrase }
    );

    const description = this.translateService.instant(
      'TargetCtrl_DeletePromptDescription'
    );

    const modalOptions = {
      title,
      deleteInstructions,
      deletePhrase,
      description,
      confirmButtonText: this.translateService.instant('Core_Confirm'),
    };

    this.modalService
      .showDeleteConfirmation(modalOptions)
      .pipe(
        switchMap(() => {
          setting.values.forEach((s) => {
            s.value = '';
          });

          return this.orgSettingsService.updateSettings(
            this.orgId,
            [setting],
            this.configurationValue,
            this.selectedLocaleId,
            true
          );
        }),
        // update languages dropdown
        tap(() => {
          // remove the configured localeId now that it's been deleted
          const index = this.setting.configuredLocaleIds.indexOf(
            this.selectedLocaleId
          );
          this.setting.configuredLocaleIds.splice(index, 1);

          this.setupLanguages().subscribe(() => {
            this.cdr.detectChanges();
          });
        })
      )
      .subscribe();
  }

  public checkCustomSetting(settingId: SettingId): boolean {
    return this.setting.id === settingId;
  }

  public selectedLocaleIsConfigured() {
    return this.setting.configuredLocaleIds?.includes(this.selectedLocaleId);
  }

  public onLanguageSelected(value: ComboboxOption) {
    this.selectedLocaleId = value.id as string;
    this.selectedLanguageName = this.systemLanguages.find(
      (l) => l.id === value.id
    ).name;
    this.reloadingSetting = true;
    this.orgSettingsService
      .getSetting(this.orgId, this.setting.id, this.selectedLocaleId)
      .subscribe((setting: OrgSetting) => {
        this.setting = setting;
        this.reloadingSetting = false;
        this.cdr.detectChanges();
      });
    this.cdr.detectChanges();
  }

  // this is hardcoded to en-US for now until we have the ability to set the default language (coming soon)
  private setupLanguages(
    selectedLocaleId: string = 'en-US'
  ): Observable<LanguageLocale[]> {
    return this.languageService.getFullSystemLanguages().pipe(
      tap((languages: LanguageLocale[]) => {
        this.systemLanguages = languages
          .map(
            (l: LanguageLocale) =>
              ({
                name: l.name,
                id: l.lcid,
                ...(this.setting.configuredLocaleIds?.includes(l.lcid) && {
                  groupingId: 'assigned',
                  groupingLabel: this.i18n.Core_Active,
                }),
              }) as Option
          )
          .sort((a, b) => {
            // First, compare based on 'groupingId' existence
            const aGrouped = !!a.groupingId;
            const bGrouped = !!b.groupingId;

            if (aGrouped && !bGrouped) {
              return -1; // 'a' (grouped) comes before 'b' (ungrouped)
            } else if (!aGrouped && bGrouped) {
              return 1; // 'b' (grouped) comes before 'a' (ungrouped)
            } else {
              // Both are in the same group (both grouped or both ungrouped), sort alphabetically
              return a.name.localeCompare(b.name);
            }
          });

        this.setDefaultLanguage(selectedLocaleId);
      })
    );
  }

  private setDefaultLanguage(selectedLocaleId: string): void {
    const defaultLanguage = this.systemLanguages.find(
      (l) => l.id === selectedLocaleId
    );
    this.selectedLocaleId = defaultLanguage.id.toString();

    // this is all garbage, when the component is redone we should have a better pattern for handling
    // a change of the default language using signals etc
    this.onLanguageSelected({ id: this.selectedLocaleId });
  }
}
